import React, { useState } from 'react';
import { Box, Popover, IconButton, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { MIN_ERP_LEAD_DAYS, startsTooSoonForErp, earliestErpStartDate } from '../../constants/experiments';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EditIcon from '@mui/icons-material/Edit';
import { format, addWeeks, parseISO } from 'date-fns';

// Shared typography styles to match ExperimentsTable
const sharedFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';
const sharedLetterSpacing = '-0.01em';

const InlineDatePicker = ({ experimentId, dateFrom, dateTo, onDraftChange, t }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [startDate, setStartDate] = useState(dateFrom ? parseISO(dateFrom) : new Date());
  const [endDate, setEndDate] = useState(dateTo ? parseISO(dateTo) : addWeeks(new Date(), 8));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleStartDateChange = (newDate) => {
    setStartDate(newDate);
    const newEndDate = addWeeks(newDate, 8);
    setEndDate(newEndDate);

    // Draft change instead of immediate update
    if (onDraftChange) {
      onDraftChange(experimentId, {
        dateFrom: format(newDate, 'yyyy-MM-dd'),
        dateTo: format(newEndDate, 'yyyy-MM-dd')
      });
    }
    handleClose(); // Close popover after selection
  };

  const handleEndDateChange = (newDate) => {
    setEndDate(newDate);

    // Draft change instead of immediate update
    if (onDraftChange) {
      onDraftChange(experimentId, {
        dateTo: format(newDate, 'yyyy-MM-dd')
      });
    }
    handleClose(); // Close popover after selection
  };

  // Format date range like ExperimentsTable: "Jul 1-15" or "Jul 25-Aug 8"
  // Can accept either Date objects or ISO strings
  const formatDateRange = (startInput, endInput) => {
    if (!startInput || !endInput) return '-';

    try {
      // Handle both Date objects and ISO strings
      const start = startInput instanceof Date ? startInput : parseISO(startInput);
      const end = endInput instanceof Date ? endInput : parseISO(endInput);

      const startDay = start.getDate();
      const endDay = end.getDate();

      // Get translated month names if t function available
      const monthNames = t ? [
        t('january', { defaultValue: 'January' }),
        t('february', { defaultValue: 'February' }),
        t('march', { defaultValue: 'March' }),
        t('april', { defaultValue: 'April' }),
        t('may', { defaultValue: 'May' }),
        t('june', { defaultValue: 'June' }),
        t('july', { defaultValue: 'July' }),
        t('august', { defaultValue: 'August' }),
        t('september', { defaultValue: 'September' }),
        t('october', { defaultValue: 'October' }),
        t('november', { defaultValue: 'November' }),
        t('december', { defaultValue: 'December' })
      ] : [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];

      const startMonthIndex = start.getMonth();
      const endMonthIndex = end.getMonth();
      const startMonth = monthNames[startMonthIndex].slice(0, 3); // Get first 3 characters
      const endMonth = monthNames[endMonthIndex].slice(0, 3);

      // If same month, show "Jul 1-15"
      if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        return `${startMonth} ${startDay}-${endDay}`;
      }

      // If different months, show "Jul 25-Aug 8"
      return `${startMonth} ${startDay}-${endMonth} ${endDay}`;
    } catch {
      return '-';
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
            borderRadius: 1
          },
          padding: '2px 4px'
        }}
        onClick={handleClick}
      >
        <Typography
          component="span"
          sx={{
            fontSize: '13px',
            fontFamily: sharedFontFamily,
            fontWeight: 400,
            color: '#787774',
            letterSpacing: sharedLetterSpacing
          }}
        >
          {formatDateRange(startDate, endDate)}
        </Typography>
        <IconButton size="small" sx={{ ml: 0.5, padding: '2px' }}>
          <EditIcon sx={{ fontSize: 14, color: '#787774' }} />
        </IconButton>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: sharedFontFamily }}>
              {t ? t('experimentDates', { defaultValue: 'Experiment Dates' }) : 'Experiment Dates'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <DatePicker
                label={t ? t('startDate', { defaultValue: 'Start Date' }) : 'Start Date'}
                value={startDate}
                onChange={handleStartDateChange}
                minDate={earliestErpStartDate()}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />

              <DatePicker
                label={t ? t('endDate', { defaultValue: 'End Date' }) : 'End Date'}
                value={endDate}
                onChange={handleEndDateChange}
                minDate={startDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true
                  }
                }}
              />
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: sharedFontFamily }}>
              {t ? t('datesAutoAdjustHint', { defaultValue: 'End date auto-adjusts to +8 weeks when start date changes' }) : 'End date auto-adjusts to +8 weeks when start date changes'}
            </Typography>

            {/* La regla del ERP sólo cuando la fecha elegida la incumple */}
            {startsTooSoonForErp(startDate) && (
              <Typography variant="caption" sx={{ fontFamily: sharedFontFamily, color: '#d32f2f', fontWeight: 600, display: 'block', whiteSpace: 'normal', maxWidth: '100%' }}>
                {t ? t('erpLeadTimeRule', { days: MIN_ERP_LEAD_DAYS }) : `Start date must be at least ${MIN_ERP_LEAD_DAYS} days from today.`}
              </Typography>
            )}
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default InlineDatePicker;